import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { useSettings } from "@/contexts/SettingsContext";
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from "lucide-react";

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export default function ContactPage() {
    const { settings } = useSettings();
    const BUSINESS = settings || {};
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill in name, email, phone, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await apiClient.post("/inquiries", form);
            toast.success("Thank you! We'll be in touch within 24 hours.");
            setForm(initialForm);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send. Please try again or WhatsApp us.");
        } finally {
            setSubmitting(false);
        }
    };

    const whatsappHref = `https://wa.me/${BUSINESS.whatsapp || "919876543210"}?text=${encodeURIComponent(`Hi ${BUSINESS.business_name || "Jassi Wood Works"}, I'd like to discuss a project.`)}`;

    return (
        <div data-testid="contact-page">
            <section className="container-padded section-spacing">
                <div className="max-w-3xl">
                    <div className="overline mb-4">Get in Touch</div>
                    <h1 className="font-serif text-5xl md:text-6xl text-ink leading-[1.05]">Let's talk about what you want to build.</h1>
                    <p className="text-stone-muted mt-5 text-lg">Tell us a little about your project below — or reach us directly. We respond within 24 hours, every working day.</p>
                </div>
            </section>

            <section className="container-padded pb-20 md:pb-28">
                <div className="grid md:grid-cols-12 gap-10 md:gap-14">
                    {/* CONTACT INFO */}
                    <aside className="md:col-span-5 space-y-8">
                        <div className="border-t border-walnut/30 pt-6">
                            <div className="overline mb-2">Workshop</div>
                            <p data-testid="contact-address" className="text-ink text-lg leading-relaxed flex gap-3"><MapPin size={18} className="mt-1 shrink-0 text-walnut" /> {BUSINESS.address}</p>
                        </div>
                        <div className="border-t border-stone-border pt-6 grid sm:grid-cols-2 gap-6">
                            <a href={`tel:${BUSINESS.phone}`} data-testid="contact-phone" className="block">
                                <div className="overline mb-2">Call</div>
                                <div className="flex gap-2 text-ink hover:text-walnut transition-colors"><Phone size={18} className="text-walnut" /> {BUSINESS.phone}</div>
                            </a>
                            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="block">
                                <div className="overline mb-2">Email</div>
                                <div className="flex gap-2 text-ink hover:text-walnut transition-colors"><Mail size={18} className="text-walnut" /> {BUSINESS.email}</div>
                            </a>
                        </div>
                        <a
                            href={whatsappHref}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-whatsapp-btn"
                            className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 text-sm tracking-wide hover:opacity-90 transition-opacity"
                        >
                            <MessageCircle size={18} /> Chat on WhatsApp
                        </a>

                        <div className="border-t border-stone-border pt-6">
                            <div className="overline mb-3">Find Us</div>
                            <div className="aspect-[4/3] w-full bg-sand overflow-hidden">
                                <iframe
                                    data-testid="contact-map"
                                    title="Jassi Wood Works location"
                                    src={`https://www.google.com/maps?q=${BUSINESS.maps_query || "Mohali"}&output=embed`}
                                    className="w-full h-full border-0"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </aside>

                    {/* QUOTE FORM */}
                    <div className="md:col-span-7" id="quote">
                        <div className="bg-sand p-8 md:p-12">
                            <div className="overline mb-3">Request a Free Quote</div>
                            <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight">Tell us about your project.</h2>
                            <form onSubmit={submit} data-testid="quote-form" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <Field label="Name" required>
                                    <input
                                        data-testid="quote-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="bg-cream border border-stone-border px-4 py-3 w-full focus:outline-none focus:border-walnut"
                                    />
                                </Field>
                                <Field label="Phone" required>
                                    <input
                                        data-testid="quote-phone"
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        className="bg-cream border border-stone-border px-4 py-3 w-full focus:outline-none focus:border-walnut"
                                    />
                                </Field>
                                <Field label="Email" required className="sm:col-span-2">
                                    <input
                                        type="email"
                                        data-testid="quote-email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="bg-cream border border-stone-border px-4 py-3 w-full focus:outline-none focus:border-walnut"
                                    />
                                </Field>
                                <Field label="Service of Interest" className="sm:col-span-2">
                                    <select
                                        data-testid="quote-service"
                                        value={form.service}
                                        onChange={(e) => setForm({ ...form, service: e.target.value })}
                                        className="bg-cream border border-stone-border px-4 py-3 w-full focus:outline-none focus:border-walnut"
                                    >
                                        <option value="">Select a service…</option>
                                        {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                                        <option value="Other">Other</option>
                                    </select>
                                </Field>
                                <Field label="Project Details" required className="sm:col-span-2">
                                    <textarea
                                        rows={5}
                                        data-testid="quote-message"
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        className="bg-cream border border-stone-border px-4 py-3 w-full focus:outline-none focus:border-walnut resize-none"
                                        placeholder="Rough scope, timeline, budget range, location…"
                                    />
                                </Field>
                                <div className="sm:col-span-2">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="quote-submit"
                                        className="bg-walnut text-cream px-8 py-4 text-sm tracking-wider uppercase hover:bg-walnut-dark transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                                    >
                                        {submitting && <Loader2 size={16} className="animate-spin" />} Send Inquiry
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="text-xs uppercase tracking-widest text-stone-muted block mb-2">{label}{required && <span className="text-walnut">*</span>}</span>
            {children}
        </label>
    );
}
